import React, {
  useCallback, useContext, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { useLocation } from '@thd-olt-component-react/router';
import { ProductResultsContext } from './ProductResultsContext';
import {
  getClearAllWithSort,
  getAppliedRefinements,
  getSortNameValue,
  formatToFriendlyDisplayCount
} from '../product-results-helpers';
import { getFirstNonEmptyObject } from '../utils/searchHelpers';
import { AppliedFiltersChips } from './AppliedFiltersChips/AppliedFiltersChips';
import { ResultsSortBy } from './ResultsSortBy';
import { ProductResultsSearchMethodsPLP } from '../utils/product-results-search-methods-PLP';
import { getFilterAnalyticsLabel, getChipText } from '../utils/fulfillment-helpers';

const ResultsApplied = ({
  onAppliedChange,
  onDisplay,
  onDisplayChange,
  viewPrimaryFilters,
  hideResultsCount,
  isGetItFastOnPlp,
  data: dataProp
}) => {
  const { data: dataContext } = useContext(ProductResultsContext);
  const isPlpSpeedEnabled = useConfigService('fs-prop:isPlpSpeedEnabled');
  const theme = useTheme(ResultsApplied);
  const { hideResultsApplied } = theme.props;
  const [cancelClick, setCancelClick] = useState(false);
  const data = getFirstNonEmptyObject(dataContext, dataProp);
  const { appliedDimensions = [], metadata, searchReport } = data?.searchModel || {};
  const {
    keyword = '', sortBy = '', sortOrder = '', totalProducts = 0
  } = searchReport || {};

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  // Temporary (PLP speed A/B test):
  // Sort handler is wired up here for the A/B test. Remove after test completes.
  const { onSortChangePLP } = ProductResultsSearchMethodsPLP();
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;
  const onSortChange = useCallback((sortParams) => {
    onSortChangePLP({ ...sortParams, path });
  }, [path]);

  const desktopSort = useRef(getSortNameValue({
    key: (sortBy && sortBy.toLowerCase()) || '',
    order: (sortOrder && sortOrder.toLowerCase()) || '',
    keyword
  }).value);

  let appliedRefinements = getAppliedRefinements(appliedDimensions, isGetItFastOnPlp);

  const handleSortOrderChange = (sortorder) => {
    if (sortorder) {
      desktopSort.current = `${sortorder.output.sortby}:${sortorder.output.sortorder}`;
    }
  };

  const handleClearAllClick = () => {
    document.body.classList.add('sui-opacity-50');
    setTimeout(() => {
      let count = 0;
      let url = '';
      getClearAllWithSort({ metadata, searchReport }).split('?').forEach((key) => {
        if (count >= 0) {
          url += (count === 0 ? key : (count === 1 ? '?' : '&') + key);
        }
        count += 1;
      });
      const refinement = {
        url,
        clear: true
      };
      if (onAppliedChange) {
        onAppliedChange({ refinement, onDisplay: 'false' });
      }

      // fire plp-filters.click with all currently applied refinements as removed
      const removed = appliedDimensions.flatMap((appliedDimension) => appliedDimension.refinements
        .map((ref) => ({
          type: appliedDimension.label,
          value: ref.label,
          label: isPlpSpeedEnabled
            ? getFilterAnalyticsLabel(appliedDimension.label, ref.label, isPlpSpeedEnabled)
            : getChipText(appliedDimension.label, ref.label, isPlpSpeedEnabled)[1],
          filterType: 'primary'
        })));
      if (removed.length > 0) {
        window.LIFE_CYCLE_EVENT_BUS?.trigger('plp-filters.click', { added: [], removed });
      }
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('sortorder.value', handleSortOrderChange);
  }, [desktopSort]);

  useEffect(() => {
    // Removal of filters : upon click of 'x', trigger below event
    if (cancelClick) {
      if (appliedDimensions && appliedDimensions.length > 0) {
        // no-op: chip deselect analytics are handled directly in handleFiltersChipChange
      } else {
        // applied and removed 1st filter
        handleClearAllClick();
      }
      setCancelClick(false);
    }
  }, [appliedDimensions, cancelClick]);

  if (!data) return null;

  const handleOnDisplayChange = () => {
    const removed = [{ type: 'Get It Fast', value: 'On Display', label: 'On Display', filterType: 'primary' }];
    // when onDisplay filters is removed from chip list
    window.LIFE_CYCLE_EVENT_BUS?.trigger('plp-filters.click', {
      added: [],
      removed: removed
    });
    onDisplayChange({ useStoreFilter: 'false', multiStoreIds: [] });
  };

  if (onDisplay === 'true') {
    appliedRefinements.push({
      dimensionName: 'Get It Fast',
      refinementLabel: 'On Display',
      refinementKey: ''
    });
  }

  const handleFiltersChipChange = (props) => {
    const { refinement, dimension } = props;
    // apiDimensionLabel/apiRefinementLabel come directly from getAppliedRefinements (raw API values)
    const apiDimensionLabel = dimension.apiDimensionLabel || dimension.label;
    const apiRefinementLabel = refinement.apiRefinementLabel || refinement.label;
    const removed = [{
      type: apiDimensionLabel,
      value: apiRefinementLabel,
      label: isPlpSpeedEnabled
        ? getFilterAnalyticsLabel(apiDimensionLabel, apiRefinementLabel, isPlpSpeedEnabled)
        : refinement.label,
      filterType: 'primary'
    }];
    window.LIFE_CYCLE_EVENT_BUS?.trigger('plp-filters.click', {
      added: [],
      removed
    });
    setCancelClick(true);
    onAppliedChange(props);
  };

  // Hide product results for exchange users
  if (hideResultsApplied) {
    return null;
  }

  const hasProducts = totalProducts != null;

  // Temporary (PLP speed A/B test):
  // When hideResultsCount is true and PLP speed is enabled, show the results
  // count below the chips rather than hiding it. This workaround compensates
  // for the current Fusion slot configuration during the A/B test.
  // Remove after A/B test. If successful, reconfigure Fusion slots directly.
  const showCountBelow = hasProducts && isPlpSpeedEnabled && hideResultsCount;

  const showCountAbove = hasProducts && !isPlpSpeedEnabled && !hideResultsCount;

  const showChips = !viewPrimaryFilters;

  if (!showCountAbove && !showChips && !showCountBelow) {
    return null;
  }

  const resultsCountClasses = classNames(
    'sui-flex sui-flex-row sui-flex-wrap sui-gap-2',
    {
      'md:sui-pt-4 sui-justify-between sui-items-center': isPlpSpeedEnabled
    }
  );

  const friendlyDisplayCount = formatToFriendlyDisplayCount(totalProducts);

  const resultsCountBlock = (
    <div
      id="plp-total-results-count-container"
      data-testid={`plp-results-count-container-${showCountBelow ? 'bottom' : 'top'}`}
      className={resultsCountClasses}
    >
      <Typography
        id="plp-total-results-count"
        data-plp-total-results-count={totalProducts}
        aria-label={`${friendlyDisplayCount} product results`}
        variant={showCountBelow ? 'h2' : 'body-lg'}
        weight="bold"
        height="tight"
      >
        {`${friendlyDisplayCount} results`}
      </Typography>
      {isPlpSpeedEnabled && (
        <ResultsSortBy
          onSortChange={onSortChange}
          searchModelData={data}
        />
      )}
    </div>
  );

  return (

    <div className="sui-flex sui-flex-col sui-w-full" data-component="ResultsApplied" data-testid="resultsApplied">
      <div className="sui-overflow-x-auto">
        {showCountAbove && resultsCountBlock}
        {showChips && (
          <AppliedFiltersChips
            appliedRefinements={appliedRefinements}
            desktopSort={desktopSort.current}
            enableClearAll={appliedRefinements.length > 1}
            isMobile={isMobile}
            onClearAll={handleClearAllClick}
            onDisplay={onDisplay}
            onDisplayChange={handleOnDisplayChange}
            onFiltersChipChange={handleFiltersChipChange}
            isPlpSpeedEnabled={isPlpSpeedEnabled}
          />
        )}
        {showCountBelow && resultsCountBlock}
      </div>
    </div>
  );
};

ResultsApplied.propTypes = {
  onAppliedChange: PropTypes.func.isRequired,
  onDisplay: PropTypes.string,
  onDisplayChange: PropTypes.func,
  viewPrimaryFilters: PropTypes.bool,
  hideResultsCount: PropTypes.bool,
  isGetItFastOnPlp: PropTypes.bool,
  data: PropTypes.shape({})
};

ResultsApplied.defaultProps = {
  onDisplay: 'false',
  onDisplayChange: null,
  viewPrimaryFilters: false,
  hideResultsCount: false,
  isGetItFastOnPlp: false,
  data: undefined
};

ResultsApplied.defaultThemeProps = {
  hideResultsApplied: false
};

ResultsApplied.displayName = 'ResultsApplied';

export { ResultsApplied };
